import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Pagination,
  Skeleton,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  SelectChangeEvent,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  approveNFTsAPI,
  cancelNFTsAPI,
  getNFTsAPI,
} from "../api/launchpadAPIs";
import { NFTLaunchpad } from "../models/blockchain";
import { columns } from "../schema/nft";
import { useDebounce } from "../hooks/customHook/useDebounce";
import { getAllNftsAdmin } from "../api";
import { useLocation } from "react-router-dom";

const ITEMS_PER_PAGE = 10;

export const NFTs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState("all");
  const [searchData, setSearchData] = useState("");
  const [nfts, setNFTs] = useState<NFTLaunchpad[]>([]);
  const [loadingNFT, setLoadingNFT] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const location = useLocation();

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchData, 300);

  const handleChange = (event: SelectChangeEvent) => {
    setValue(event.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  // Fetch NFTs
  const fetchNFTs = useCallback(async () => {
    setLoadingNFT(true);
    try {
      console.log('Fetching NFTs...');
      // const res = await getNFTsAPI();
      const res = await getAllNftsAdmin();
      console.log('NFT API Response:', res);

      if (!res) {
        throw new Error('No response from API');
      }

      console.log('NFTs:', res);

      let arr = await res?.data?.map((item: any, index: number) => {
        // Get the unique identifier
        const id = item.id || item.nftId || index;

        // Get owner information
        const owner = item.ownerId || item.owner || "";

        // Get name from nested metadata structure
        const name = item.metadata?.properties?.player?.name ||
          item.metadata?.name ||
          item.name ||
          "";

        // Convert Firestore timestamp to JS Date
        const rawTime = item.updateAt?._seconds ||
          item.createAt?._seconds ||
          item.time ||
          null;
        const time = rawTime ? new Date(rawTime * 1000) : null;

        return {
          id,
          nftStorageId: id,
          amount: item.amount || 0,
          owner,
          status: item.status || "pending",
          name,
          time,
          psc: item.psc || 0,
          minted: item.minted || 0,
          metadata: item.metadata || {},
        };
      });

      // Sort by time in descending order
      arr.sort((a: any, b: any) => {
        if (!a.time || !b.time) return 0;
        return b.time.getTime() - a.time.getTime();
      });
      setNFTs(arr);
    } catch (err) {
      console.error("Error fetching NFTs:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch NFTs. Please try again later.");
      setShowError(true);
    } finally {
      setLoadingNFT(false);
    }
  }, []);

  // Add error boundary
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchNFTs();
      } catch (error) {
        console.error('Error in useEffect:', error);
      }
    };
    fetchData();
  }, [fetchNFTs]);

  const handleCancel = async (nftStorageId: string) => {
    setLoadingNFT(true);
    try {
      await cancelNFTsAPI(nftStorageId);
      await fetchNFTs();
    } catch (error) {
      console.error(error);
      setError("Failed to cancel NFT. Please try again.");
      setShowError(true);
    } finally {
      setLoadingNFT(false);
    }
  };

  const handleApproved = async (nftStorageId: string) => {
    setLoadingNFT(true);
    try {
      await approveNFTsAPI(nftStorageId);
      await fetchNFTs();
    } catch (error) {
      console.error(error);
      setError("Failed to approve NFT. Please try again.");
      setShowError(true);
    } finally {
      setLoadingNFT(false);
    }
  };

  // Memoize filtered NFTs
  const filteredNFTs = useMemo(() => {
    let filtered = [...nfts];

    // Apply status filter
    if (value !== "all") {
      filtered = filtered.filter(item => item?.status === value);
    }

    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        nft =>
          nft?.nftStorageId?.toLowerCase().includes(query) ||
          nft?.ownerId?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [nfts, value, debouncedSearchQuery]);

  // Memoize paginated NFTs
  const paginatedNFTs = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredNFTs.slice(startIndex, startIndex + pageSize);
  }, [filteredNFTs, page, pageSize]);

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Loading skeleton rows
  const loadingRows = useMemo(() => {
    return Array.from({ length: pageSize }, (_, index) => ({
      id: `loading-${index}`,
      nftStorageId: <Skeleton animation="wave" />,
      amount: <Skeleton animation="wave" />,
      owner: <Skeleton animation="wave" />,
      status: <Skeleton animation="wave" />,
      name: <Skeleton animation="wave" />,
      time: <Skeleton animation="wave" />,
      actions: <Skeleton animation="wave" />,
    }));
  }, [pageSize]);

  // Loading skeleton columns
  const loadingColumns = useMemo(() => [
    { field: 'nftStorageId', headerName: 'NFT ID', width: 200, renderCell: () => <Skeleton animation="wave" /> },
    { field: 'amount', headerName: 'Amount', width: 150, renderCell: () => <Skeleton animation="wave" /> },
    { field: 'owner', headerName: 'Owner', width: 200, renderCell: () => <Skeleton animation="wave" /> },
    { field: 'status', headerName: 'Status', width: 150, renderCell: () => <Skeleton animation="wave" /> },
    { field: 'name', headerName: 'Name', width: 200, renderCell: () => <Skeleton animation="wave" /> },
    { field: 'time', headerName: 'Time', width: 200, renderCell: () => <Skeleton animation="wave" /> },
    { field: 'actions', headerName: 'Actions', width: 150, renderCell: () => <Skeleton animation="wave" /> },
  ], []);

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: 2,
        overflow: 'hidden',
        minHeight: '70vh',
        position: 'relative'
      }}
    >
      <Box sx={{ width: "100%", typography: "body1" }}>
        {/* Header with Title and Filters */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            className="font-montserrat800"
            sx={{ fontWeight: 'bold' }}
          >
            All NFTs
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: { xs: "100%", md: "auto" }, flexGrow: { md: 1, lg: 0 } }}
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                value={value}
                onChange={handleChange}
                label="Status"
                disabled={loadingNFT}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <TextField
              placeholder="Search by NFT ID, owner, or name"
              value={searchData}
              onChange={(e) => setSearchData(e.target.value)}
              size="small"
              fullWidth
              label="Search"
              disabled={loadingNFT}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: { sm: '300px', md: '400px' } }}
            />
          </Stack>
        </Stack>

        {/* Data Grid with Loading State */}
        {loadingNFT ? (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={loadingRows}
              columns={loadingColumns}
              hideFooter
              disableColumnMenu
              disableSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.background.default,
                  fontWeight: 'bold',
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                },
              }}
            />
          </Box>
        ) : (
          <DataGrid
            rows={paginatedNFTs}
            columns={columns(handleCancel, handleApproved, location.pathname)}
            loading={false}
            autoHeight
            disableSelectionOnClick
            disableColumnMenu
            components={{
              Toolbar: GridToolbar,
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: false,
                printOptions: { disableToolbarButton: true },
                csvOptions: { disableToolbarButton: !isTablet },
              },
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.default,
                fontWeight: 'bold',
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: `${theme.palette.primary.light || '#f5f5f5'}`,
              },
            }}
          />
        )}

        {/* Pagination */}
        {!loadingNFT && filteredNFTs.length > 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "center", sm: "center" }}
            sx={{ mt: 3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                Items per page:
              </Typography>
              <Select
                value={pageSize.toString()}
                onChange={handlePageSizeChange}
                size="small"
                sx={{ minWidth: 70 }}
                disabled={loadingNFT}
              >
                <MenuItem value="5">5</MenuItem>
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="25">25</MenuItem>
                <MenuItem value="50">50</MenuItem>
              </Select>
            </Box>

            <Pagination
              count={Math.ceil(filteredNFTs.length / pageSize)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
              disabled={loadingNFT}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '4px',
                  margin: '0 2px',
                },
                '& .Mui-selected': {
                  backgroundColor: '#1976d2 !important',
                  color: 'white !important',
                  '&:hover': {
                    backgroundColor: '#1565c0 !important',
                  },
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              Showing {((page - 1) * pageSize) + 1}-
              {Math.min(page * pageSize, filteredNFTs.length)} of {filteredNFTs.length} NFTs
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
        >
          {error}
        </Alert>
      </Snackbar>
    </Paper>
  );
};
